// Правка договора: номер, даты, юрлицо-исполнитель.
//
// Отдельной карточки договора в разделе нет намеренно — шесть полей целиком помещаются в
// карточку заказчика. Но поправить их было нечем: у договора, как и у заказчика, на сервере был
// только путь «завести». Номер договора при этом переписывают с бумаги, а даты набирают на
// слух с телефонного разговора.
//
// ЗАКАЗЧИК ЗДЕСЬ НЕ МЕНЯЕТСЯ. К договору привязаны объекты, а через объекты — ведомости:
// перевод договора другому заказчику это не правка поля, а разговор с бумагами. То же решение
// принято по объекту, и по той же причине.
//
// Юрлицо менять можно, и это не мелочь: от него зависит, в чью ведомость попадут люди объектов
// этого договора. Выбрать его при заведении можно было из пяти похожих названий одним нажатием.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiErrorWarningLine, RiFileTextLine } from '@remixicon/react';

import type { ApiError } from '@/shared/errors/api-error';
import type { ContractPatch } from '@/shared/api/endpoints/org';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { CheckboxField } from '@/shared/ui/checkbox-field';
import { DateField } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { EmptyState, Skeleton } from '@/shared/ui/screen-state';
import { SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';
import { useContractsQuery, useLegalEntitiesQuery } from '@/shared/org/queries';

import { usePatchContractMutation } from '../queries';

/** Правка договора — то же право, что и заведение (server/app/api/v1/dictionaries.py). */
const EDIT_PERMISSION = 'core.dictionary.edit';

export function EditContractPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const права = useAuthStore((state) => state.permissions);
  const можно = can(права, EDIT_PERMISSION);
  // Отдельного адреса «один договор» в контракте нет, поэтому строка берётся из списка. Список
  // берётся полный: панель адресуема ссылкой, и открыть её могут не из карточки заказчика.
  const договоры = useContractsQuery({});
  const юрлица = useLegalEntitiesQuery();
  const правка = usePatchContractMutation(id);

  const [черновик, setЧерновик] = useState<{
    номер: string;
    юрлицо: string;
    подписан: string;
    сДаты: string;
    поДату: string;
    действует: boolean;
  } | null>(null);
  const [ошибка, setОшибка] = useState<string | undefined>(undefined);
  const [заслон, setЗаслон] = useState<string | null>(null);

  if (договоры.data === undefined) {
    return (
      <div className='flex flex-col gap-3'>
        <Skeleton className='h-10 w-full' />
        <Skeleton className='h-10 w-full' />
        <Skeleton className='h-10 w-2/3' />
      </div>
    );
  }

  const договор = договоры.data.items.find((строка) => строка.id === id);
  if (договор === undefined) {
    return (
      <EmptyState
        icon={RiFileTextLine}
        title='Этого договора больше нет в справочнике'
        description='Ссылка ведёт на договор, которого не видно в списке: либо её прислали до того, как справочник поменяли, либо у вас нет доступа к справочникам. Вернитесь к карточке заказчика.'
      />
    );
  }

  const поля = черновик ?? {
    номер: договор.number ?? '',
    юрлицо: договор.legal_entity_id,
    подписан: договор.signed_at ?? '',
    сДаты: договор.date_from ?? '',
    поДату: договор.date_to ?? '',
    действует: договор.is_active,
  };

  function поставить<К extends keyof typeof поля>(ключ: К, значение: (typeof поля)[К]) {
    setЧерновик({ ...поля, [ключ]: значение });
  }

  function сохранить(событие: FormEvent<HTMLFormElement>) {
    событие.preventDefault();
    if (!можно) {
      setЗаслон(
        'Править договоры может тот, у кого есть право на справочники. Попросите администратора выдать его — набранное никуда не денется.',
      );
      return;
    }
    if (правка.isPending) {
      setЗаслон('Договор уже сохраняется, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }
    // Срок задом наперёд — это опечатка в одной цифре года, и её надо поймать здесь: сервер
    // такой договор примет, а объект по нему перестанет считаться действующим молча.
    if (поля.сДаты !== '' && поля.поДату !== '' && поля.поДату < поля.сДаты) {
      setОшибка('Дата окончания раньше начала: проверьте год.');
      return;
    }

    setОшибка(undefined);
    setЗаслон(null);
    const тело: ContractPatch = {
      number: поля.номер.trim() === '' ? null : поля.номер.trim(),
      legal_entity_id: поля.юрлицо,
      // Пустая дата уходит как «убрать», а не как «не трогать»: договор без срока окончания —
      // законное состояние, и вернуть его человек обязан уметь.
      signed_at: поля.подписан === '' ? null : поля.подписан,
      date_from: поля.сДаты === '' ? null : поля.сДаты,
      date_to: поля.поДату === '' ? null : поля.поДату,
      is_active: поля.действует,
    };
    правка.mutate(тело, { onSuccess: onClose });
  }

  return (
    <form onSubmit={сохранить} noValidate className='flex flex-col gap-3'>
      {/* Заказчик назван прямо, потому что он единственное поле, которого в форме нет: без
          этой строки человек ищет его глазами и решает, что забыли. */}
      <p className='text-paragraph-xs text-text-sub-600'>
        Заказчик: {договор.customer_name}. Он не меняется — договор с объектами и ведомостями
        привязан именно к нему.
      </p>

      {!можно && (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>
            Права на правку справочников у вас нет: форму можно заполнить, но сохранить договор не
            выйдет. Право выдаёт администратор.
          </span>
        </Alert.Root>
      )}

      <TextField
        id='edit-contract-number'
        label='Номер договора'
        icon={RiFileTextLine}
        value={поля.номер}
        onChange={(значение) => поставить('номер', значение)}
        placeholder='Д-0001'
        hint='Пусто — договор так и останется «без номера».'
      />

      <SelectField
        id='edit-contract-legal-entity'
        label='Юрлицо-исполнитель'
        required
        value={поля.юрлицо}
        onChange={(значение) => поставить('юрлицо', значение)}
        options={(юрлица.data?.items ?? []).map((строка) => ({
          value: строка.id,
          label: строка.name,
        }))}
        placeholder='Выберите юрлицо'
        pending={юрлица.isPending}
        failed={юрлица.isError}
        failedError={юрлица.error as ApiError | null}
        emptyNote='Юрлиц в справочнике нет: их заводит администратор в настройках.'
        hint='От него зависит, в чью ведомость попадут люди объектов этого договора.'
      />

      <DateField
        id='edit-contract-signed'
        label='Подписан'
        value={поля.подписан}
        onChange={(значение) => поставить('подписан', значение)}
      />

      <div className='grid grid-cols-2 gap-2'>
        <DateField
          id='edit-contract-from'
          label='Действует с'
          value={поля.сДаты}
          onChange={(значение) => поставить('сДаты', значение)}
        />
        <DateField
          id='edit-contract-to'
          label='Действует по'
          value={поля.поДату}
          onChange={(значение) => поставить('поДату', значение)}
          error={ошибка}
          hint='Пусто — без срока окончания.'
        />
      </div>

      <CheckboxField
        id='edit-contract-active'
        label='Договор действует'
        checked={поля.действует}
        onCheckedChange={(значение) => поставить('действует', значение)}
        hint='Снимите отметку, если договор закончился: он пропадёт из выбора при заведении объектов, а привязанные объекты и ведомости останутся на месте.'
      />

      {правка.isError && <ErrorAlert error={правка.error} />}

      {заслон !== null && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {заслон}
        </Hint.Root>
      )}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {правка.isPending ? 'Сохраняем…' : 'Сохранить'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
